/**
 * Ground decals — illustrated transparent PNGs (or procedural fills) that sit
 * on the grass.
 *
 * Soft PNG decals may set optional `heightFrac` so width and length can be
 * sized independently. Freeform `quadCorners` reshape is reserved for
 * hard-edged plates like the Hubert patio.
 *
 * Procedural kinds (e.g. Rounded Concrete Patio) regenerate their surface from
 * width/height so textures and control joints reflow instead of stretching.
 *
 * Unlike upright props (trees, shed, garden), decals:
 * - use a center ground anchor (no height)
 * - never cast standing shadows
 * - never scale with character Y-depth
 * - always paint beneath vertical props and the character
 * - may optionally block walking (footprint)
 * - may use a freeform quad so each side angle is independently editable
 *   (clip-path). Soft PNG alpha edges should omit `quadCorners` so the
 *   art’s transparency is not cut into a hard polygon.
 */

import type { SpawnPoint, WalkObstacle, WorldSceneryLayer } from '@/world-prototype/types';
import type { LayoutProfilePair } from '@/world-prototype/camera/backyardPresentation';
import {
  localOffsetsToWorld,
  normalizeRotationDeg,
} from '@/world-prototype/objects/propTransform';
import {
  isQuadCorners,
  quadContainSize,
  rectQuadCorners,
  rotateQuadCorners,
  type QuadCorners,
} from '@/world-prototype/objects/quadShape';
import {
  spriteHeightFrac,
  type PlaceObjectOptions,
  type WorldObjectSprite,
} from '@/world-prototype/objects/placeObject';
import {
  isRoundedConcretePatioProcedural,
  resolveRoundedConcretePatioSrc,
  type GroundProceduralKind,
} from '@/world-prototype/objects/roundedConcretePatio';

/**
 * Paint depth band for ground decals.
 * Character / upright props use depthFromY ≈ 100–200; decals stay below (~25–80).
 */
export function groundDecalDepthFromY(y: number): number {
  return Math.round(25 + y * 55);
}

/** One reusable painted ground feature (gravel, mulch, path, …). */
export interface GroundDecalDef {
  id: string;
  title: string;
  /** Center of the decal in world coords (0–1). */
  x: number;
  y: number;
  /**
   * Rendered width as a fraction of world width (the decal’s scale).
   * Prefer this field; `scale` is an optional alias with the same meaning.
   */
  widthFrac: number;
  /**
   * Optional rendered height as a fraction of world height.
   * When omitted, height follows the sprite’s natural aspect.
   * Soft PNG decals (gravel) set this so the oval can be stretched
   * wider/longer without freeform reshape.
   */
  heightFrac?: number;
  /**
   * Optional alias for `widthFrac` (fraction of world width).
   * When set, overrides `widthFrac` at place time.
   */
  scale?: number;
  /** Per-presentation visual transforms (desktop / mobile). */
  layouts?: LayoutProfilePair;
  /**
   * Finished transparent PNG — texture + edges + alpha live in the art.
   * For procedural kinds this is a fallback / serialize placeholder; the
   * live layer `src` is regenerated from width/height.
   */
  sprite: WorldObjectSprite;
  /**
   * Optional procedural surface (regenerates on resize). Omit for PNG art.
   */
  procedural?: GroundProceduralKind;
  /**
   * Rotation in degrees around the center.
   * Positive = clockwise (CSS). Default 0.
   * Baked into `shapeCorners` at place time when a freeform quad is used.
   */
  rotationDeg?: number;
  /** Horizontal mirror around the center. Omitted / false = as authored. */
  flipX?: boolean;
  /** Override the Y-derived paint depth (still keep below upright props). */
  depth?: number;
  /** Toggle without deleting the definition. */
  enabled?: boolean;
  /**
   * Optional collision footprint as offsets from the center.
   * Empty / omitted = walkable (default for gravel pads, paths, etc.).
   */
  footprint?: readonly SpawnPoint[];
  /**
   * Freeform quad (TL→TR→BR→BL) as local offsets from center.
   * Layout profiles may override via `layouts.*.quadCorners`.
   * Omit for soft-edged PNG art (gravel) so alpha is not hard-clipped.
   */
  quadCorners?: QuadCorners;
}

export interface PlacedGroundDecal {
  sceneryLayers: readonly WorldSceneryLayer[];
  walkObstacle: WalkObstacle | null;
}

/** Resolved width / scale for a decal def. */
export function groundDecalScale(def: GroundDecalDef): number {
  return def.scale ?? def.widthFrac;
}

/**
 * Expand a GroundDecalDef into scenery + optional collision.
 * PNG kinds use authored art; procedural kinds regenerate from size.
 */
export function placeGroundDecal(
  def: GroundDecalDef,
  options: PlaceObjectOptions,
): PlacedGroundDecal {
  if (def.enabled === false) {
    return { sceneryLayers: [], walkObstacle: null };
  }

  const depth = def.depth ?? groundDecalDepthFromY(def.y);
  const rotationDeg = normalizeRotationDeg(def.rotationDeg ?? 0);
  const flipX = def.flipX === true;
  const widthFrac = groundDecalScale(def);
  const height =
    def.heightFrac != null && def.heightFrac > 0
      ? def.heightFrac
      : spriteHeightFrac(widthFrac, def.sprite, options.worldAspect);

  const hasFreeform = isQuadCorners(def.quadCorners);
  const localCorners: QuadCorners = hasFreeform
    ? [
        { ...def.quadCorners![0] },
        { ...def.quadCorners![1] },
        { ...def.quadCorners![2] },
        { ...def.quadCorners![3] },
      ]
    : rectQuadCorners(widthFrac / 2, height / 2);

  // CSS scaleX handles visual flip; keep authored corners unflipped so clip +
  // image stay aligned under the same transform.
  const rotatedCorners = rotateQuadCorners(
    localCorners,
    rotationDeg,
    options.worldAspect,
  );
  const contain = hasFreeform
    ? quadContainSize(rotatedCorners)
    : { width: widthFrac, height };

  let src = def.sprite.src;
  const procedural = isRoundedConcretePatioProcedural(def.procedural);
  if (procedural) {
    const paintW = hasFreeform
      ? Math.max(widthFrac, contain.width)
      : widthFrac;
    const paintH = hasFreeform ? Math.max(height, contain.height) : height;
    src = resolveRoundedConcretePatioSrc(
      def.id,
      {
        widthFrac: paintW,
        heightFrac: paintH,
        worldAspect: options.worldAspect,
        silhouette: hasFreeform ? 'rect' : 'rounded-top',
      },
      def.sprite.src,
    ).src;
  }

  // Freeform quads need a clip-path. Soft PNG / procedural decals without
  // freeform use CSS rotation only so alpha edges are preserved.
  const layer: WorldSceneryLayer = {
    id: def.id,
    kind: 'ground-decal',
    zIndex: depth,
    depth,
    src,
    alt: def.sprite.alt,
    x: def.x,
    y: def.y,
    width: hasFreeform ? Math.max(widthFrac, contain.width) : widthFrac,
    height: hasFreeform ? Math.max(height, contain.height) : height,
    rotationDeg: rotationDeg || undefined,
    ...(flipX ? { flipX: true } : {}),
    ...(hasFreeform
      ? {
          shapeCorners: rotatedCorners,
          shapeCornersLocal: localCorners,
          ...(procedural ? { shapeCornerStyle: 'top' as const } : {}),
        }
      : {}),
    placeholderClass: `wp-layer-${def.id}`,
  };

  const footprint = def.footprint ?? [];
  const footprintLocal = localOffsetsToWorld(
    footprint,
    rotationDeg,
    options.worldAspect,
    flipX,
  );
  const walkObstacle =
    footprintLocal.length >= 3
      ? {
          id: def.id,
          polygon: footprintLocal.map((p) => ({
            x: def.x + p.x,
            y: def.y + p.y,
          })),
        }
      : null;

  return {
    sceneryLayers: [layer],
    walkObstacle,
  };
}

/** Place many ground decals for easy space wiring. */
export function placeGroundDecals(
  defs: readonly GroundDecalDef[],
  options: PlaceObjectOptions,
): {
  sceneryLayers: WorldSceneryLayer[];
  walkObstacles: WalkObstacle[];
} {
  const sceneryLayers: WorldSceneryLayer[] = [];
  const walkObstacles: WalkObstacle[] = [];
  for (const def of defs) {
    const placed = placeGroundDecal(def, options);
    sceneryLayers.push(...placed.sceneryLayers);
    if (placed.walkObstacle) walkObstacles.push(placed.walkObstacle);
  }
  return { sceneryLayers, walkObstacles };
}
